/**
 * Journey map file attachments (S3-backed, tracked in toolbox_db).
 *
 * Upload is a three-step handshake so the bytes never pass through the API:
 *
 *   1. POST `db/journey-map/{id}/files/presign/`  -> short-lived signed PUT + object key
 *   2. PUT the file straight at storage with the content type the presign returned
 *   3. POST `db/journey-map/{id}/files/`          -> creates the row; the server independently
 *                                                    re-reads the object's real size and
 *                                                    content type off storage before saving
 *
 * Step 3 is what makes step 2 trustworthy: a client cannot invent an object key or misreport
 * a file's size, because the row is only written after the server has looked at the object.
 *
 * `uploadJourneyMapFile` falls back to a single multipart POST when the direct PUT fails,
 * which is what happens behind a proxy or CSP that blocks cross-origin PUTs. The backend
 * applies the same content type and size rules on either path.
 *
 * Reads are signed GET URLs fetched per click (`db/journey-map-file/{id}/download-url/`).
 * They expire, so they are never held in component state.
 */
import { buildApiUrl, getApiHeaders } from '../queriesHelpers';
import { UploadProgressCallback, putFileToStorageWithProgress } from '../xhrUpload';

export type JourneyMapFileRecord = {
  id: number;
  journey_map: number;
  journey_map_item?: number | null;
  object_key: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  description?: string | null;
  version: number;
  is_active: boolean;
  is_previewable?: boolean;
  metadata?: Record<string, any>;
  uploaded_by?: number | null;
  uploaded_by_email?: string | null;
  journey_map_name?: string | null;
  journey_map_item_name?: string | null;
  created?: string | null;
  last_updated?: string | null;
};

export type JourneyMapFilePresign = {
  upload_url: string;
  object_key: string;
  content_type: string;
  file_name: string;
  expires_in: number;
  max_bytes: number;
};

export type JourneyMapFileDownload = {
  url: string;
  expires_in: number;
  refresh_after: number;
  signed: boolean;
  file_name: string;
  content_type: string;
  size_bytes: number;
  version: number;
};

/** The S3 bucket is unset, so the whole attachment feature is inert (API answers 503). */
export class JourneyMapFileStorageUnavailableError extends Error {}

const filesEndpoint = (journeyMapId: number | string) => `db/journey-map/${journeyMapId}/files/`;

const fileEndpoint = (fileId: number | string) => `db/journey-map-file/${fileId}/`;

/**
 * Every attachment endpoint normalizes its error body to `detail`, but field-level
 * serializer errors still come back keyed by field, so check those too.
 */
const errorMessage = (payload: any, status: number, fallback: string): string => {
  const detail =
    payload?.detail ?? payload?.object_key ?? payload?.file_name ?? payload?.journey_map;

  if (Array.isArray(detail) && detail.length) return String(detail[0]);
  if (detail) return String(detail);
  if (typeof payload === 'string' && payload.trim()) return payload;

  return `${fallback} (${status})`;
};

async function fileApiFetch<T = any>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  endpoint: string,
  options: { params?: Record<string, any>; body?: any; form?: FormData } = {},
): Promise<T> {
  const { params = {}, body, form } = options;

  const headers = { ...getApiHeaders(null, method, endpoint) } as Record<string, string>;

  // FormData sets its own multipart boundary, so the JSON content type must not be sent.
  if (form) delete headers['Content-Type'];

  const response = await fetch(buildApiUrl(endpoint, params, true), {
    method,
    headers,
    credentials: 'include',
    ...(form ? { body: form } : {}),
    ...(body === undefined || form ? {} : { body: JSON.stringify(body) }),
  });

  const text = await response.text();

  let parsed: any = null;

  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!response.ok) {
    const message = errorMessage(parsed, response.status, 'Journey map file request failed');

    if (response.status === 503) {
      throw new JourneyMapFileStorageUnavailableError(message);
    }

    const error: any = new Error(message);
    error.status = response.status;
    error.payload = parsed;
    throw error;
  }

  return parsed as T;
}

export async function listJourneyMapFiles(
  journeyMapId: number | string,
  params: { journeyMapItemId?: number | string | null; includeInactive?: boolean } = {},
): Promise<JourneyMapFileRecord[]> {
  const query: Record<string, any> = {};

  if (params.journeyMapItemId != null && params.journeyMapItemId !== '') {
    query.journey_map_item = params.journeyMapItemId;
  }

  if (params.includeInactive) {
    query.include_inactive = 1;
  }

  const payload = await fileApiFetch<any>('GET', filesEndpoint(journeyMapId), { params: query });

  if (Array.isArray(payload)) return payload as JourneyMapFileRecord[];
  if (Array.isArray(payload?.items)) return payload.items as JourneyMapFileRecord[];

  return [];
}

export async function presignJourneyMapFileUpload(
  journeyMapId: number | string,
  file: File,
): Promise<JourneyMapFilePresign> {
  return await fileApiFetch<JourneyMapFilePresign>(
    'POST',
    `db/journey-map/${journeyMapId}/files/presign/`,
    {
      body: {
        file_name: file.name,
        content_type: file.type || 'application/octet-stream',
      },
    },
  );
}

/**
 * PUT the file at the presigned URL. Uses XHR (not fetch) so `onProgress` can report real
 * upload progress; the signature still covers the method, key and content type only, so we send
 * no API auth headers (that would leak the Toolbox token to storage and invalidate the signature).
 */
async function putToStorage(
  presign: JourneyMapFilePresign,
  file: File,
  onProgress?: UploadProgressCallback,
): Promise<void> {
  await putFileToStorageWithProgress(
    presign.upload_url,
    file,
    presign.content_type || file.type || 'application/octet-stream',
    onProgress,
  );
}

type UploadOptions = {
  journeyMapItemId?: number | string | null;
  description?: string;
  /** Reports upload progress (0–100) for a determinate progress bar. */
  onProgress?: UploadProgressCallback;
};

/** Create a new attachment row for *file*, uploading the bytes first. */
export async function uploadJourneyMapFile(
  journeyMapId: number | string,
  file: File,
  options: UploadOptions = {},
): Promise<JourneyMapFileRecord> {
  const presign = await presignJourneyMapFileUpload(journeyMapId, file);

  if (presign.max_bytes && file.size > presign.max_bytes) {
    throw new Error(
      `${file.name} is larger than the ${Math.round(presign.max_bytes / (1024 * 1024))} MB limit.`,
    );
  }

  try {
    await putToStorage(presign, file, options.onProgress);
  } catch {
    // A blocked direct PUT is a network/policy problem, not a bad file: let the API stream it.
    return await uploadJourneyMapFileMultipart(journeyMapId, file, options);
  }

  const payload: Record<string, any> = {
    object_key: presign.object_key,
    file_name: presign.file_name || file.name,
  };

  if (options.journeyMapItemId != null && options.journeyMapItemId !== '') {
    payload.journey_map_item = options.journeyMapItemId;
  }

  if (options.description) {
    payload.description = options.description;
  }

  return await fileApiFetch<JourneyMapFileRecord>('POST', filesEndpoint(journeyMapId), {
    body: payload,
  });
}

/** Single-request fallback: the API receives the bytes and writes them to storage itself. */
async function uploadJourneyMapFileMultipart(
  journeyMapId: number | string,
  file: File,
  options: UploadOptions = {},
): Promise<JourneyMapFileRecord> {
  const form = new FormData();
  form.append('file', file);
  form.append('file_name', file.name);

  if (options.journeyMapItemId != null && options.journeyMapItemId !== '') {
    form.append('journey_map_item', String(options.journeyMapItemId));
  }

  if (options.description) {
    form.append('description', options.description);
  }

  return await fileApiFetch<JourneyMapFileRecord>('POST', filesEndpoint(journeyMapId), { form });
}

/**
 * Replace the bytes behind an existing attachment.
 *
 * A new `object_key` on an existing row is what the API reads as a replacement: it bumps
 * `version` and removes the superseded object, so anything referencing this file id keeps
 * working and starts serving the new content.
 */
export async function replaceJourneyMapFile(
  journeyMapId: number | string,
  fileId: number | string,
  file: File,
  onProgress?: UploadProgressCallback,
): Promise<JourneyMapFileRecord> {
  const presign = await presignJourneyMapFileUpload(journeyMapId, file);
  await putToStorage(presign, file, onProgress);

  return await fileApiFetch<JourneyMapFileRecord>('PATCH', fileEndpoint(fileId), {
    body: {
      object_key: presign.object_key,
      file_name: presign.file_name || file.name,
    },
  });
}

export async function updateJourneyMapFile(
  fileId: number | string,
  changes: {
    description?: string;
    journey_map_item?: number | string | null;
    file_name?: string;
  },
): Promise<JourneyMapFileRecord> {
  return await fileApiFetch<JourneyMapFileRecord>('PATCH', fileEndpoint(fileId), { body: changes });
}

/** Remove the object and deactivate the row. The API refuses if the object survives. */
export async function deleteJourneyMapFile(fileId: number | string): Promise<void> {
  await fileApiFetch<null>('DELETE', fileEndpoint(fileId));
}

export async function getJourneyMapFileDownload(
  fileId: number | string,
  options: { inline?: boolean } = {},
): Promise<JourneyMapFileDownload> {
  return await fileApiFetch<JourneyMapFileDownload>(
    'GET',
    `db/journey-map-file/${fileId}/download-url/`,
    { params: options.inline ? { inline: 1 } : {} },
  );
}

/**
 * Open a file in a new tab (previewable types) or hand it to the browser's downloader.
 *
 * The signed URL already carries the right Content-Disposition, so an anchor click is enough;
 * nothing is buffered into a blob, which keeps large files off the JS heap.
 */
export async function openJourneyMapFile(
  record: Pick<JourneyMapFileRecord, 'id' | 'file_name' | 'is_previewable'>,
  options: { inline?: boolean } = {},
): Promise<void> {
  const inline = options.inline ?? Boolean(record.is_previewable);
  const download = await getJourneyMapFileDownload(record.id, { inline });

  if (inline) {
    window.open(download.url, '_blank', 'noopener,noreferrer');
    return;
  }

  const anchor = document.createElement('a');
  anchor.href = download.url;
  anchor.download = download.file_name || record.file_name || '';
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

export function formatFileSize(bytes?: number | null): string {
  const value = Number(bytes || 0);

  if (!Number.isFinite(value) || value <= 0) return '';

  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size >= 10 || unitIndex === 0 ? Math.round(size) : size.toFixed(1)} ${units[unitIndex]}`;
}
